import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import {
  LayoutDashboard, ShoppingBag, Trophy, Vote, Settings as SettingsIcon,
  Download, Lock, LogOut, FileQuestion, Plus, Trash2, CheckCircle2,
  BarChart3, Users, Crown, AlertTriangle,
} from 'lucide-react';

const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
const PIN_KEY = '0gb:adminpin';

export default function Admin() {
  const [pin, setPin] = useState(() => sessionStorage.getItem(PIN_KEY) || '');
  const [authed, setAuthed] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [tab, setTab] = useState('overview');
  const [eventId, setEventId] = useState(null);

  useEffect(() => {
    api.getConfig().then((c) => setEventId(c.event.id)).catch(() => {});
  }, []);

  useEffect(() => {
    if (pin && eventId) {
      api.adminLogin(pin).then(() => setAuthed(true)).catch(() => { setAuthed(false); setPin(''); sessionStorage.removeItem(PIN_KEY); });
    }
  }, [pin, eventId]);

  const login = async () => {
    try {
      await api.adminLogin(pinInput);
      sessionStorage.setItem(PIN_KEY, pinInput);
      setPin(pinInput);
      setAuthed(true);
    } catch { toast.error('Invalid PIN'); }
  };

  const logout = () => { sessionStorage.removeItem(PIN_KEY); setPin(''); setAuthed(false); setPinInput(''); };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#070C1E] px-6">
        <div className="w-full max-w-sm bg-[#111A38] border border-[#D4AF37]/25 rounded-2xl p-8 text-center">
          <Lock size={32} className="text-[#D4AF37] mx-auto" />
          <h1 className="font-serif text-2xl text-[#FDFBF7] mt-3">Admin Access</h1>
          <p className="text-xs text-slate-400 mt-1 mb-5">0g Baltics Event Platform</p>
          <input
            data-testid="admin-pin-input"
            type="password"
            inputMode="numeric"
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && login()}
            placeholder="PIN"
            className="w-full bg-[#070C1E] border border-[#D4AF37]/30 rounded-lg p-3.5 text-center text-lg font-mono text-slate-100 tracking-widest focus:border-[#D4AF37] outline-none"
          />
          <button data-testid="admin-login-button" onClick={login} className="mt-4 w-full bg-gradient-to-r from-[#D4AF37] to-[#E5C158] text-[#070C1E] font-bold py-3.5 rounded-xl">Enter</button>
        </div>
      </div>
    );
  }

  const TABS = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'orders', label: 'Orders', icon: ShoppingBag },
    { id: 'quiz', label: 'Quiz', icon: Trophy },
    { id: 'analytics', label: 'Tasting Analytics', icon: BarChart3 },
    { id: 'questions', label: 'Questions', icon: FileQuestion },
    { id: 'voting', label: 'Voting', icon: Vote },
    { id: 'settings', label: 'Settings', icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen bg-[#070C1E] text-slate-200">
      <header className="border-b border-[#D4AF37]/20 px-6 py-4 flex justify-between items-center">
        <h1 className="font-serif text-xl text-[#E5C158]">Event Admin <span className="text-slate-500 text-sm font-sans">· BCFL × Pommery Riga</span></h1>
        <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-1 text-sm text-slate-400 hover:text-[#E5C158]"><LogOut size={16} /> Logout</button>
      </header>

      <div className="flex flex-wrap gap-2 px-6 py-4 border-b border-[#D4AF37]/10">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`admin-tab-${id}`}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${tab === id ? 'bg-[#D4AF37] text-[#070C1E] font-semibold' : 'bg-[#111A38] text-slate-300'}`}
          >
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      <div className="p-6 max-w-6xl">
        {eventId && tab === 'overview' && <Overview pin={pin} eventId={eventId} />}
        {eventId && tab === 'orders' && <Orders pin={pin} eventId={eventId} />}
        {eventId && tab === 'quiz' && <Quiz pin={pin} eventId={eventId} />}
        {eventId && tab === 'analytics' && <TastingAnalytics pin={pin} eventId={eventId} />}
        {eventId && tab === 'questions' && <Questions pin={pin} eventId={eventId} />}
        {eventId && tab === 'voting' && <Voting pin={pin} eventId={eventId} />}
        {eventId && tab === 'settings' && <SettingsPanel pin={pin} eventId={eventId} />}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#111A38] border border-[#D4AF37]/20 rounded-xl p-5">
      <p className="font-mono text-[10px] tracking-widest uppercase text-slate-500">{label}</p>
      <p className="font-serif text-4xl text-[#E5C158] mt-1">{value}</p>
    </div>
  );
}

function Highlight({ label, value, sub, accent }) {
  return (
    <div className={`rounded-xl p-4 border ${accent ? 'bg-[#18244D] border-[#D4AF37]/40' : 'bg-[#111A38] border-[#D4AF37]/20'}`}>
      <p className="font-mono text-[10px] tracking-widest uppercase text-slate-500">{label}</p>
      <p className="font-serif text-xl text-[#FDFBF7] mt-1 leading-tight">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function Overview({ pin, eventId }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.adminOverview(eventId, pin).then(setD).catch(() => {}), [eventId, pin]);
  useEffect(() => { load(); const id = setInterval(load, 6000); return () => clearInterval(id); }, [load]);
  if (!d) return <p className="text-slate-500">Loading…</p>;
  return (
    <div data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Participants" value={d.participants} />
        <Stat label="Tasting participants" value={d.tasting_participants} />
        <Stat label="Quiz participants" value={d.quiz_participants} />
        <Stat label="Votes" value={d.votes} />
        <Stat label="Bottle requests" value={d.order_requests} />
        <Stat label="Total bottles" value={d.total_bottles} />
      </div>

      <h3 className="font-serif text-lg text-[#E5C158] mt-8 mb-3">Event summary</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Highlight label="Top-rated cuvée" accent
          value={d.top_rated_cuvee ? d.top_rated_cuvee.name : '—'}
          sub={d.top_rated_cuvee ? `${d.top_rated_cuvee.average} / 5 average` : 'No ratings yet'} />
        <Highlight label="Audience favourite" accent
          value={d.audience_favourite ? d.audience_favourite.name : '—'}
          sub={d.audience_favourite ? `${d.audience_favourite.votes} favourites` : 'No votes yet'} />
        <Highlight label="Quiz winner" accent
          value={d.quiz_winner ? (d.quiz_winner.tie ? 'TIE — manual decision' : d.quiz_winner.name) : '—'}
          sub={d.quiz_winner ? `${d.quiz_winner.score}/${d.quiz_winner.total} · ${fmtTime(d.quiz_winner.duration_seconds)}` : 'No attempts yet'} />
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <div className="bg-[#111A38] border border-[#D4AF37]/20 rounded-xl p-5">
          <h3 className="font-serif text-lg text-[#E5C158] mb-3">Bottles by cuvée</h3>
          {d.bottle_breakdown.length === 0 ? <p className="text-sm text-slate-500">No requests yet.</p> :
            d.bottle_breakdown.map((b) => (
              <div key={b.name} className="flex justify-between text-sm py-1.5 border-b border-[#D4AF37]/10">
                <span className="text-slate-300">{b.name}</span><span className="font-mono text-[#E5C158]">{b.quantity}</span>
              </div>
            ))}
        </div>
        <div className="bg-[#111A38] border border-[#D4AF37]/20 rounded-xl p-5">
          <h3 className="font-serif text-lg text-[#E5C158] mb-3">Votes by cuvée</h3>
          {d.vote_breakdown.map((b) => (
            <div key={b.product_id} className="flex justify-between text-sm py-1.5 border-b border-[#D4AF37]/10">
              <span className="text-slate-300">{b.name}</span><span className="font-mono text-[#E5C158]">{b.votes} · {b.percentage}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TastingAnalytics({ pin, eventId }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.adminTastingAnalytics(eventId, pin).then(setD).catch(() => {}), [eventId, pin]);
  useEffect(() => { load(); const id = setInterval(load, 8000); return () => clearInterval(id); }, [load]);
  const exportCsv = async () => {
    try {
      const res = await fetch(api.adminEventExportUrl(eventId, 'tasting'), { headers: { 'X-Admin-Pin': pin } });
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = 'tasting_analytics.csv'; a.click();
    } catch { toast.error('Export failed'); }
  };
  if (!d) return <p className="text-slate-500">Loading…</p>;
  return (
    <div data-testid="admin-analytics" className="max-w-5xl">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <h2 className="font-serif text-xl text-[#E5C158]">Tasting Analytics <span className="text-xs text-slate-500 font-sans">· anonymous & aggregated</span></h2>
        <button data-testid="analytics-export-button" onClick={exportCsv} className="flex items-center gap-2 bg-[#D4AF37] text-[#070C1E] font-semibold px-4 py-2 rounded-lg text-sm"><Download size={15} /> Export CSV</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Unique tasters" value={d.unique_participants} />
        <Stat label="Completed profiles" value={d.completed_profiles} />
        <Stat label="Partial profiles" value={d.partial_profiles} />
        <Stat label="Total ratings" value={d.total_ratings} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {d.products.map((p) => (
          <div key={p.product_id} data-testid={`analytics-cuvee-${p.order}`} className="bg-[#111A38] border border-[#D4AF37]/20 rounded-xl p-5">
            <div className="flex justify-between items-start">
              <h3 className="font-serif text-lg text-[#FDFBF7] leading-tight pr-2">{p.name}</h3>
              <div className="text-right shrink-0">
                <p className="font-serif text-2xl text-[#E5C158]">{p.average || '—'}<span className="text-sm text-slate-500"> /5</span></p>
                <p className="font-mono text-[10px] text-slate-500">{p.ratings} ratings</p>
              </div>
            </div>

            {/* distribution */}
            <div className="mt-3 space-y-1">
              {[5, 4, 3, 2, 1].map((n) => {
                const c = p.distribution[String(n)] || 0;
                const pct = p.ratings ? Math.round(c / p.ratings * 100) : 0;
                return (
                  <div key={n} className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-slate-500 w-3">{n}</span>
                    <div className="flex-1 bg-[#070C1E] h-2 rounded-full overflow-hidden border border-[#D4AF37]/15">
                      <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#E5C158]" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="font-mono text-[10px] text-slate-500 w-6 text-right">{c}</span>
                  </div>
                );
              })}
            </div>

            {p.top_aromas.length > 0 && (
              <div className="mt-4">
                <p className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-1.5">Top aromas</p>
                <div className="space-y-1">
                  {p.top_aromas.map((a) => (
                    <div key={a.tag} className="flex justify-between text-xs">
                      <span className="text-slate-300 capitalize">{a.tag.replace(/_/g, ' ')}</span>
                      <span className="font-mono text-[#E5C158]">{a.percentage}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-between mt-4 pt-3 border-t border-[#D4AF37]/10 text-xs">
              <span className="text-slate-400">❤ {p.favourites} favourites</span>
              <span className="text-slate-400">{p.participation}% of tasters rated</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Orders({ pin, eventId }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.adminOrders(eventId, pin).then(setData).catch(() => {}), [eventId, pin]);
  useEffect(() => { load(); }, [load]);
  const update = async (id, status) => { await api.adminUpdateOrder(id, status, pin); load(); toast.success('Updated'); };
  const del = async (id) => {
    if (!window.confirm('Delete this bottle request? This cannot be undone.')) return;
    await api.adminDeleteOrder(id, pin); toast.success('Request deleted'); load();
  };
  const exportCsv = async () => {
    try {
      const url = api.adminExportUrl(eventId, pin);
      const res = await fetch(url, { headers: { 'X-Admin-Pin': pin } });
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = 'bottle_requests.csv'; a.click();
    } catch { toast.error('Export failed'); }
  };
  if (!data) return <p className="text-slate-500">Loading…</p>;
  return (
    <div data-testid="admin-orders">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-serif text-xl text-[#E5C158]">Bottle Requests ({data.orders.length})</h2>
        <button data-testid="admin-export-button" onClick={exportCsv} className="flex items-center gap-2 bg-[#D4AF37] text-[#070C1E] font-semibold px-4 py-2 rounded-lg text-sm"><Download size={15} /> Export CSV</button>
      </div>
      {data.orders.length === 0 ? <p className="text-slate-500">No requests yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-500 font-mono text-[10px] uppercase tracking-widest border-b border-[#D4AF37]/20">
              <th className="py-2 pr-3">Name</th><th className="pr-3">Contact</th><th className="pr-3">Cuvées</th><th className="pr-3">Qty</th><th className="pr-3">Status</th><th className="pr-3"></th>
            </tr></thead>
            <tbody>
              {data.orders.map((o) => (
                <tr key={o.id} className="border-b border-[#D4AF37]/10 align-top">
                  <td className="py-3 pr-3 text-slate-200">{o.full_name}<br /><span className="text-[10px] text-slate-500">{o.comment}</span></td>
                  <td className="pr-3 text-slate-400 text-xs">{o.email}<br />{o.phone}</td>
                  <td className="pr-3 text-slate-300 text-xs">{o.items.map((i) => `${i.product_name} ×${i.quantity}`).join(', ')}</td>
                  <td className="pr-3 font-mono text-[#E5C158]">{o.total_quantity}</td>
                  <td className="pr-3">
                    <select data-testid={`admin-order-status-${o.id}`} value={o.status} onChange={(e) => update(o.id, e.target.value)} className="bg-[#070C1E] border border-[#D4AF37]/30 rounded px-2 py-1 text-xs text-slate-200">
                      {data.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="pr-3">
                    <button data-testid={`admin-order-delete-${o.id}`} onClick={() => del(o.id)} title="Delete request" className="text-[#E63946] hover:bg-[#E63946]/10 rounded p-1.5"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Toggle({ label, value, onChange, testId }) {
  return (
    <button data-testid={testId} onClick={() => onChange(!value)} className="flex items-center justify-between w-full bg-[#111A38] border border-[#D4AF37]/20 rounded-xl px-4 py-3">
      <span className="text-sm text-slate-200">{label}</span>
      <span className={`w-11 h-6 rounded-full transition-all relative ${value ? 'bg-[#D4AF37]' : 'bg-slate-700'}`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${value ? 'left-[22px]' : 'left-0.5'}`} />
      </span>
    </button>
  );
}

function Quiz({ pin, eventId }) {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const load = useCallback(async () => {
    try {
      setData(await api.adminQuiz(eventId, pin));
      setSettings(await api.adminGetSettings(eventId, pin));
    } catch { /* */ }
  }, [eventId, pin]);
  useEffect(() => { load(); const id = setInterval(load, 6000); return () => clearInterval(id); }, [load]);
  const patch = async (changes) => { const s = await api.adminUpdateSettings(eventId, changes, pin); setSettings(s); load(); };
  const exportCsv = async () => {
    try {
      const res = await fetch(api.adminEventExportUrl(eventId, 'quiz'), { headers: { 'X-Admin-Pin': pin } });
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = 'quiz_results.csv'; a.click();
    } catch { toast.error('Export failed'); }
  };
  if (!settings || !data) return <p className="text-slate-500">Loading…</p>;
  const { participants, leader } = data;
  const closed = !settings.quiz_open;
  const delAttempt = async (id) => {
    if (!window.confirm('Delete this quiz result? This cannot be undone.')) return;
    await api.adminDeleteAttempt(id, pin); toast.success('Result deleted'); load();
  };

  return (
    <div data-testid="admin-quiz">
      {/* Leader / Winner card */}
      {leader ? (
        <div data-testid="admin-leader-card" className={`rounded-2xl p-5 mb-5 border ${leader.tie ? 'bg-[#3a1e1e] border-[#E63946]/50' : 'bg-gradient-to-br from-[#18244D] to-[#111A38] border-[#D4AF37]/50'}`}>
          {leader.tie ? (
            <div className="flex items-start gap-3" data-testid="admin-leader-tie">
              <AlertTriangle size={26} className="text-[#E63946] shrink-0" />
              <div>
                <p className="font-mono text-[10px] tracking-widest uppercase text-[#E63946]">Tie — manual decision required</p>
                <p className="text-sm text-slate-300 mt-1">Two participants share the same score <span className="font-mono text-[#E5C158]">{leader.score}/{leader.total}</span> and time <span className="font-mono text-[#E5C158]">{fmtTime(leader.duration_seconds)}</span>. Choose the winner manually.</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <Crown size={30} className="text-[#D4AF37] shrink-0" />
              <div className="flex-1">
                <p className="font-mono text-[10px] tracking-widest uppercase text-[#E5C158]">{closed ? 'Winner' : 'Current leader'}</p>
                <p className="font-serif text-2xl text-[#FDFBF7] leading-tight">{leader.first_name} {leader.last_name}</p>
                <p className="text-xs text-slate-400 mt-0.5">{leader.email} · {leader.phone}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-serif text-3xl text-[#E5C158]">{leader.score}/{leader.total}</p>
                <p className="font-mono text-xs text-slate-400">{fmtTime(leader.duration_seconds)}</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <p className="text-slate-500 mb-5">No official attempts yet.</p>
      )}

      {/* Live controls */}
      <div className="flex flex-wrap gap-3 mb-6">
        <button
          data-testid="admin-close-quiz"
          onClick={() => patch({ quiz_open: !settings.quiz_open })}
          className={`px-5 py-3 rounded-xl font-semibold text-sm ${closed ? 'bg-[#2D6A4F] text-white' : 'bg-[#E63946] text-white'}`}
        >
          {closed ? 'Reopen quiz' : 'Close quiz'}
        </button>
        <button
          data-testid="admin-reveal-winner"
          onClick={() => patch({ winner_revealed: !settings.winner_revealed })}
          disabled={leader?.tie && !settings.winner_revealed}
          className={`px-5 py-3 rounded-xl font-semibold text-sm border ${settings.winner_revealed ? 'bg-[#D4AF37] text-[#070C1E] border-[#E5C158]' : 'bg-transparent text-[#E5C158] border-[#D4AF37]/50'} disabled:opacity-40`}
        >
          {settings.winner_revealed ? 'Winner revealed ✓' : 'Reveal winner'}
        </button>
        <button data-testid="admin-quiz-export" onClick={exportCsv} className="flex items-center gap-2 px-4 py-3 rounded-xl bg-[#111A38] border border-[#D4AF37]/40 text-[#E5C158] font-semibold text-sm"><Download size={15} /> Export CSV</button>
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-6 max-w-2xl">
        <Toggle testId="admin-leaderboard" label="Leaderboard visible (guests)" value={settings.leaderboard_visible} onChange={(v) => patch({ leaderboard_visible: v })} />
      </div>

      <h2 className="font-serif text-xl text-[#E5C158] mb-3">Ranking ({participants.length})</h2>
      {participants.length === 0 ? <p className="text-slate-500">No participants yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-500 font-mono text-[10px] uppercase tracking-widest border-b border-[#D4AF37]/20">
              <th className="py-2 pr-3">#</th><th className="pr-3">Name</th><th className="pr-3">Email</th><th className="pr-3">Phone</th><th className="pr-3">Score</th><th className="pr-3">Time</th><th className="pr-3"></th>
            </tr></thead>
            <tbody>
              {participants.map((p) => (
                <tr key={p.rank} className={`border-b border-[#D4AF37]/10 ${p.rank === 1 ? 'bg-[#D4AF37]/5' : ''}`}>
                  <td className="py-2.5 pr-3 font-mono text-[#D4AF37]">{p.rank}</td>
                  <td className="pr-3 text-slate-200">{p.first_name} {p.last_name}</td>
                  <td className="pr-3 text-slate-400 text-xs">{p.email}</td>
                  <td className="pr-3 text-slate-400 text-xs">{p.phone}</td>
                  <td className="pr-3 font-mono text-[#E5C158]">{p.score}/{p.total}</td>
                  <td className="pr-3 font-mono text-slate-400">{fmtTime(p.duration_seconds)}</td>
                  <td className="pr-3">
                    <button data-testid={`admin-attempt-delete-${p.id}`} onClick={() => delAttempt(p.id)} title="Delete result" className="text-[#E63946] hover:bg-[#E63946]/10 rounded p-1.5"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Voting({ pin, eventId }) {
  const [overview, setOverview] = useState(null);
  const [settings, setSettings] = useState(null);
  const load = useCallback(async () => {
    try { setOverview(await api.adminOverview(eventId, pin)); setSettings(await api.adminGetSettings(eventId, pin)); } catch { /* */ }
  }, [eventId, pin]);
  useEffect(() => { load(); const id = setInterval(load, 6000); return () => clearInterval(id); }, [load]);
  const patch = async (changes) => { const s = await api.adminUpdateSettings(eventId, changes, pin); setSettings(s); };
  const resetVotes = async () => {
    if (!window.confirm('Reset ALL votes for this event? This permanently clears the audience favourites.')) return;
    const r = await api.adminResetVotes(eventId, pin);
    toast.success(`Votes reset (${r.deleted} cleared)`); load();
  };
  if (!settings || !overview) return <p className="text-slate-500">Loading…</p>;
  return (
    <div data-testid="admin-voting">
      <div className="grid md:grid-cols-2 gap-3 mb-6 max-w-2xl">
        <Toggle testId="admin-voting-enabled" label="Voting enabled" value={settings.voting_enabled} onChange={(v) => patch({ voting_enabled: v })} />
        <Toggle testId="admin-votes-revealed" label="Reveal result" value={settings.votes_revealed} onChange={(v) => patch({ votes_revealed: v })} />
      </div>
      <div className="flex justify-between items-center mb-3">
        <h2 className="font-serif text-xl text-[#E5C158]">Votes by cuvée · {overview.votes} total</h2>
        <button data-testid="admin-reset-votes" onClick={resetVotes} className="flex items-center gap-2 text-[#E63946] border border-[#E63946]/50 rounded-lg px-3 py-2 text-xs font-semibold"><Trash2 size={14} /> Reset votes</button>
      </div>
      <div className="max-w-2xl space-y-3">
        {[...overview.vote_breakdown].sort((a, b) => b.votes - a.votes).map((r) => (
          <div key={r.product_id}>
            <div className="flex justify-between text-sm mb-1"><span className="text-slate-200">{r.name}</span><span className="font-mono text-slate-400">{r.votes} · {r.percentage}%</span></div>
            <div className="w-full bg-[#111A38] h-3 rounded-full overflow-hidden border border-[#D4AF37]/15"><div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#E5C158]" style={{ width: `${r.percentage}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Questions({ pin, eventId }) {
  const [questions, setQuestions] = useState([]);
  const [settings, setSettings] = useState(null);
  const emptyDraft = { category: 'fundamentals', q: '', options: ['', '', '', ''], correct: 0, status: 'EDITORIAL_VALIDATION_REQUIRED', active: true };
  const [draft, setDraft] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.adminQuestions(eventId, pin); setQuestions(r.questions);
      setSettings(await api.adminGetSettings(eventId, pin));
    } catch { /* */ }
  }, [eventId, pin]);
  useEffect(() => { load(); }, [load]);

  const validatedCount = questions.filter((q) => q.status === 'VALIDATED' && q.active).length;

  const saveExisting = async (q) => {
    await api.adminUpdateQuestion(q.id, { category: q.category, q: q.question, options: q.options, correct: q.correct, status: q.status, active: q.active }, pin);
    toast.success('Saved'); load();
  };
  const patchLocal = (id, changes) => setQuestions((qs) => qs.map((q) => q.id === id ? { ...q, ...changes } : q));
  const setOption = (id, i, val) => setQuestions((qs) => qs.map((q) => q.id === id ? { ...q, options: q.options.map((o, j) => j === i ? val : o) } : q));
  const remove = async (id) => { await api.adminDeleteQuestion(id, pin); toast.success('Deleted'); load(); };
  const createQ = async () => {
    if (!draft.q.trim() || draft.options.some((o) => !o.trim())) { toast.error('Fill question and all 4 options'); return; }
    await api.adminCreateQuestion(eventId, { category: draft.category, q: draft.q, options: draft.options, correct: draft.correct, status: draft.status, active: draft.active }, pin);
    toast.success('Question added'); setDraft(null); load();
  };
  const togglePatch = async (changes) => { const ns = await api.adminUpdateSettings(eventId, changes, pin); setSettings(ns); };

  if (!settings) return <p className="text-slate-500">Loading…</p>;

  return (
    <div data-testid="admin-questions" className="max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-serif text-xl text-[#E5C158]">Question Bank ({questions.length}) · <span className="text-[#2D6A4F]">{validatedCount} validated & active</span></h2>
        <button data-testid="admin-add-question" onClick={() => setDraft({ ...emptyDraft })} className="flex items-center gap-2 bg-[#D4AF37] text-[#070C1E] font-semibold px-4 py-2 rounded-lg text-sm"><Plus size={15} /> Add question</button>
      </div>

      <div className="mb-5 max-w-md">
        <Toggle testId="admin-require-validated" label="Production mode: only VALIDATED questions in official attempts" value={settings.require_validated_questions} onChange={(v) => togglePatch({ require_validated_questions: v })} />
        {settings.require_validated_questions && validatedCount < (settings.quiz_questions_per_attempt || 15) && (
          <p className="text-[11px] text-[#F4A261] mt-2">Warning: only {validatedCount} validated & active — fewer than {settings.quiz_questions_per_attempt || 15} per attempt.</p>
        )}
      </div>

      {draft && (
        <div className="bg-[#0B132B] border border-[#D4AF37]/40 rounded-xl p-4 mb-5" data-testid="admin-question-draft">
          <QuestionEditor
            q={{ ...draft, question: draft.q }}
            onField={(c) => setDraft((d) => ({ ...d, ...(c.question !== undefined ? { q: c.question } : c) }))}
            onOption={(i, val) => setDraft((d) => ({ ...d, options: d.options.map((o, j) => j === i ? val : o) }))}
          />
          <div className="flex gap-2 mt-3">
            <button onClick={createQ} className="bg-[#2D6A4F] text-white font-semibold px-4 py-2 rounded-lg text-sm">Create</button>
            <button onClick={() => setDraft(null)} className="text-slate-400 px-4 py-2 text-sm">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {questions.map((q) => (
          <div key={q.id} data-testid={`admin-question-${q.id}`} className="bg-[#111A38] border border-[#D4AF37]/20 rounded-xl p-4">
            <QuestionEditor q={q} onField={(c) => patchLocal(q.id, c)} onOption={(i, val) => setOption(q.id, i, val)} />
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <button
                data-testid={`admin-validate-${q.id}`}
                onClick={() => { patchLocal(q.id, { status: q.status === 'VALIDATED' ? 'EDITORIAL_VALIDATION_REQUIRED' : 'VALIDATED' }); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${q.status === 'VALIDATED' ? 'bg-[#2D6A4F] text-white' : 'bg-[#18244D] text-[#F4A261] border border-[#F4A261]/40'}`}
              >
                <CheckCircle2 size={14} /> {q.status === 'VALIDATED' ? 'Validated' : 'Needs validation'}
              </button>
              <button
                data-testid={`admin-active-${q.id}`}
                onClick={() => patchLocal(q.id, { active: !q.active })}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${q.active ? 'bg-[#D4AF37] text-[#070C1E]' : 'bg-slate-700 text-slate-300'}`}
              >
                {q.active ? 'Active' : 'Inactive'}
              </button>
              <button onClick={() => saveExisting(q)} className="bg-[#111A38] border border-[#D4AF37]/40 text-[#E5C158] px-3 py-1.5 rounded-lg text-xs font-semibold ml-auto">Save</button>
              <button data-testid={`admin-delete-${q.id}`} onClick={() => remove(q.id)} className="text-[#E63946] p-1.5"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const CATEGORIES = ['fundamentals', 'grape_varieties', 'production', 'terminology', 'vintages', 'reims', 'pommery', 'cuvees'];

function QuestionEditor({ q, onField, onOption }) {
  return (
    <div>
      <div className="flex gap-2 mb-2">
        <select value={q.category} onChange={(e) => onField({ category: e.target.value })} className="bg-[#070C1E] border border-[#D4AF37]/30 rounded px-2 py-1.5 text-xs text-slate-200">
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {q.status && <span className={`px-2 py-1 rounded text-[10px] font-mono ${q.status === 'VALIDATED' ? 'text-[#2D6A4F]' : 'text-[#F4A261]'}`}>{q.status}</span>}
      </div>
      <textarea value={q.question} onChange={(e) => onField({ question: e.target.value })} rows={2} placeholder="Question text" className="w-full bg-[#070C1E] border border-[#D4AF37]/30 rounded-lg p-2.5 text-sm text-slate-100 outline-none focus:border-[#D4AF37] resize-none" />
      <div className="grid sm:grid-cols-2 gap-2 mt-2">
        {q.options.map((opt, i) => (
          <label key={i} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 border ${q.correct === i ? 'border-[#2D6A4F] bg-[#2D6A4F]/10' : 'border-[#D4AF37]/20'}`}>
            <input type="radio" checked={q.correct === i} onChange={() => onField({ correct: i })} className="accent-[#2D6A4F]" />
            <input value={opt} onChange={(e) => onOption(i, e.target.value)} placeholder={`Option ${i + 1}`} className="flex-1 bg-transparent text-sm text-slate-100 outline-none" />
          </label>
        ))}
      </div>
      <p className="text-[10px] text-slate-500 mt-1">Select the radio next to the correct answer.</p>
    </div>
  );
}


function SettingsPanel({ pin, eventId }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.adminGetSettings(eventId, pin).then(setS).catch(() => {}); }, [eventId, pin]);
  const patch = async (changes) => { const ns = await api.adminUpdateSettings(eventId, changes, pin); setS(ns); toast.success('Saved'); };
  if (!s) return <p className="text-slate-500">Loading…</p>;
  return (
    <div data-testid="admin-settings" className="max-w-2xl space-y-3">
      <h2 className="font-serif text-xl text-[#E5C158] mb-2">Modules</h2>
      <div className="grid md:grid-cols-2 gap-3">
        <Toggle testId="admin-orders-enabled" label="Orders enabled" value={s.orders_enabled} onChange={(v) => patch({ orders_enabled: v })} />
        <Toggle testId="admin-quiz-enabled" label="Quiz enabled" value={s.quiz_enabled} onChange={(v) => patch({ quiz_enabled: v })} />
      </div>
      <h2 className="font-serif text-xl text-[#E5C158] mt-6 mb-2">Text & links</h2>
      <TextRow label="Prize text (intro)" value={s.prize_text} onSave={(v) => patch({ prize_text: v })} />
      <TextRow label="Winner prize text" value={s.winner_prize_text} onSave={(v) => patch({ winner_prize_text: v })} />
      <TextRow label="Reims CTA label" value={s.reims_cta_label} onSave={(v) => patch({ reims_cta_label: v })} />
      <TextRow label="Reims CTA URL (blank = disabled)" value={s.reims_cta_url || ''} onSave={(v) => patch({ reims_cta_url: v || null })} />
      <TextRow label="Contact CTA label (welcome)" value={s.contact_cta_label || ''} onSave={(v) => patch({ contact_cta_label: v })} />
      <TextRow label="Contact CTA URL (blank = hidden)" value={s.contact_cta_url || ''} onSave={(v) => patch({ contact_cta_url: v || null })} />

      <DataTools pin={pin} eventId={eventId} />
    </div>
  );
}

function DataTools({ pin, eventId }) {
  const [confirming, setConfirming] = useState(false);
  const exportKind = async (kind, filename) => {
    try {
      const res = await fetch(api.adminEventExportUrl(eventId, kind), { headers: { 'X-Admin-Pin': pin } });
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = filename; a.click();
    } catch { toast.error('Export failed'); }
  };
  const purge = async () => {
    try {
      const r = await api.adminPurge(eventId, pin);
      const total = Object.values(r.deleted).reduce((s, n) => s + n, 0);
      toast.success(`Test data purged (${total} records)`);
      setConfirming(false);
    } catch { toast.error('Purge failed'); }
  };
  return (
    <>
      <h2 className="font-serif text-xl text-[#E5C158] mt-8 mb-2">Event data export</h2>
      <div className="grid sm:grid-cols-3 gap-3">
        <button data-testid="export-participants" onClick={() => exportKind('participants', 'participants.csv')} className="flex items-center justify-center gap-2 bg-[#111A38] border border-[#D4AF37]/40 text-[#E5C158] font-semibold px-4 py-3 rounded-xl text-sm"><Download size={15} /> participants.csv</button>
        <button data-testid="export-quiz" onClick={() => exportKind('quiz', 'quiz_results.csv')} className="flex items-center justify-center gap-2 bg-[#111A38] border border-[#D4AF37]/40 text-[#E5C158] font-semibold px-4 py-3 rounded-xl text-sm"><Download size={15} /> quiz_results.csv</button>
        <button data-testid="export-tasting" onClick={() => exportKind('tasting', 'tasting_analytics.csv')} className="flex items-center justify-center gap-2 bg-[#111A38] border border-[#D4AF37]/40 text-[#E5C158] font-semibold px-4 py-3 rounded-xl text-sm"><Download size={15} /> tasting_analytics.csv</button>
      </div>

      <h2 className="font-serif text-xl text-[#E63946] mt-8 mb-2 flex items-center gap-2"><AlertTriangle size={18} /> Danger zone</h2>
      <div className="bg-[#111A38] border border-[#E63946]/30 rounded-xl p-4">
        <p className="text-sm text-slate-300">Purge all QA/test event data: votes, quiz attempts, orders, tasting responses and participant contacts. Questions, content and settings are kept.</p>
        {!confirming ? (
          <button data-testid="purge-button" onClick={() => setConfirming(true)} className="mt-3 flex items-center gap-2 bg-[#E63946]/15 text-[#E63946] border border-[#E63946]/50 font-semibold px-4 py-2.5 rounded-lg text-sm"><Trash2 size={15} /> Purge test data</button>
        ) : (
          <div className="mt-3 flex gap-2">
            <button data-testid="purge-confirm" onClick={purge} className="bg-[#E63946] text-white font-semibold px-4 py-2.5 rounded-lg text-sm">Confirm purge</button>
            <button onClick={() => setConfirming(false)} className="text-slate-400 px-4 py-2.5 text-sm">Cancel</button>
          </div>
        )}
      </div>
    </>
  );
}

function TextRow({ label, value, onSave }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <div className="bg-[#111A38] border border-[#D4AF37]/20 rounded-xl p-4">
      <label className="font-mono text-[10px] tracking-widest uppercase text-slate-500">{label}</label>
      <div className="flex gap-2 mt-2">
        <input value={v} onChange={(e) => setV(e.target.value)} className="flex-1 bg-[#070C1E] border border-[#D4AF37]/30 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none focus:border-[#D4AF37]" />
        <button onClick={() => onSave(v)} className="bg-[#D4AF37] text-[#070C1E] font-semibold px-4 rounded-lg text-sm">Save</button>
      </div>
    </div>
  );
}
